<template>
  <div>
    <Navbar />
    <section class="team-top-deco" aria-hidden="true">
      <span class="deco-orb orb-left"></span>
      <span class="deco-orb orb-right"></span>
      <span class="deco-line"></span>
    </section>

    <section class="team-hero">
      <p class="hero-kicker" tabindex="0">
        Our Team
        <span class="hero-kicker-tooltip" role="tooltip">
          Meet the people behind Norland Advisory — our Board, our team, and the supporting professionals who help us deliver for every client.
        </span>
      </p>
      <h1>Team Structure</h1>
      <p>Meet the people behind Norland Advisory — our Board, our team, and the supporting professionals who help us deliver for every client.</p>
    </section>

    <section class="team-groups-section">
      <div class="team-groups-shell">
        <article v-for="group in teamGroups" :key="group.code" class="team-group-card" :style="{
          '--group-accent': group.accent,
          '--group-soft': group.soft,
          '--group-deep': group.deep
        }">
          <header class="group-header">
            <h2>{{ group.title }}</h2>
          </header>

          <div v-if="group.layout === 'avatar-circle'" class="group-member-list group-member-list-avatars">
            <article v-for="member in group.members" :key="`${group.code}-${member.name}`" class="avatar-member" :class="{ 'avatar-member-has-popover': group.title === 'Norland Team' }" tabindex="0">
              <div class="avatar-circle-wrap" :style="{ '--group-accent': group.accent }">
                <img v-if="member.photo" class="avatar-circle-img" :src="member.photo" :alt="`${member.name} portrait`" loading="lazy" />
                <span v-else class="avatar-circle-initials">{{ getInitials(member.name) }}</span>
              </div>
              <p class="avatar-member-name">{{ member.name }}</p>
              <p class="avatar-member-role" :style="{ color: group.accent }">{{ member.role }}</p>
              <div v-if="group.title === 'Norland Team'" class="member-popover" :style="{ '--group-accent': group.accent, '--group-soft': group.soft, '--group-deep': group.deep }" role="dialog" :aria-label="`${member.name} details`">
                <div class="member-popover-header">
                  <div class="member-popover-avatar">
                    <img v-if="member.photo" :src="member.photo" :alt="`${member.name} portrait`" loading="lazy" />
                    <span v-else>{{ getInitials(member.name) }}</span>
                  </div>
                  <div class="member-popover-identity">
                    <h4>{{ member.name }}</h4>
                    <p class="member-popover-role">{{ member.role }}</p>
                  </div>
                </div>
                <p v-if="member.desc" class="member-popover-desc">{{ member.desc }}</p>
                <a v-if="hasRednoteLink(member)" class="member-popover-link" :href="getRednoteLink(member)" target="_blank" rel="noopener noreferrer">
                  <span class="member-popover-link-icon" aria-hidden="true">小</span>
                  <span>View Rednote Profile</span>
                </a>
              </div>
            </article>
          </div>
          <div v-else class="group-member-list" :class="{ 'group-member-list-flat4': group.layout === 'flat4' }">
            <article v-for="member in group.members" :key="`${group.code}-${member.name}`" class="consultant-card" :class="{ 'consultant-card-basic': group.noPhoto, 'consultant-card-board': group.title === 'Norland Board' }">
              <template v-if="group.title === 'Norland Board'">
                <div class="board-top">
                  <div class="board-avatar" :style="{ '--group-accent': group.accent }">
                    <img v-if="member.photo" class="consultant-photo" :src="member.photo" :alt="`${member.name} portrait`" loading="lazy" />
                    <span v-else class="avatar-circle-initials">{{ getInitials(member.name) }}</span>
                  </div>
                  <div class="board-identity">
                    <h3>{{ member.name }}</h3>
                    <p class="consultant-role">{{ member.role }}</p>
                  </div>
                </div>
                <div class="consultant-content consultant-content-board">
                  <p v-if="group.showBio !== false && member.bio" class="consultant-bio">{{ member.bio }}</p>
                  <ul v-if="group.showDetails !== false && member.details && member.details.length" class="consultant-details">
                    <li v-for="detail in member.details" :key="`${member.name}-${detail}`">{{ detail }}</li>
                  </ul>
                  <div v-if="group.showRednote && member.rednoteLink" class="consultant-links">
                    <a :href="member.rednoteLink" target="_blank" rel="noopener noreferrer">Rednote Profile</a>
                  </div>
                  <div v-else-if="group.showRednote" class="consultant-links consultant-links-placeholder" aria-hidden="true"></div>
                  <div v-if="group.showConsultButton" class="consultant-actions">
                    <router-link to="/contact" class="consultant-consult-btn">Consultation</router-link>
                  </div>
                  <div v-if="group.showTags !== false" class="consultant-tags">
                    <span v-for="tag in member.tags" :key="`${member.name}-${tag}`">{{ tag }}</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <div v-if="!group.noPhoto && member.photo" class="consultant-photo-wrap">
                  <img class="consultant-photo" :src="member.photo" :alt="`${member.name} portrait`" loading="lazy" />
                </div>
                <div class="consultant-content">
                  <h3>{{ member.name }}</h3>
                  <p class="consultant-role">{{ member.role }}</p>
                  <p v-if="group.showBio !== false && member.bio" class="consultant-bio">{{ member.bio }}</p>
                  <ul v-if="group.showDetails !== false && member.details && member.details.length" class="consultant-details">
                    <li v-for="detail in member.details" :key="`${member.name}-${detail}`">{{ detail }}</li>
                  </ul>
                  <div v-if="group.showRednote && member.rednoteLink" class="consultant-links">
                    <a :href="member.rednoteLink" target="_blank" rel="noopener noreferrer">Rednote Profile</a>
                  </div>
                  <div v-else-if="group.showRednote" class="consultant-links consultant-links-placeholder" aria-hidden="true"></div>
                  <div v-if="group.showConsultButton" class="consultant-actions">
                    <router-link to="/contact" class="consultant-consult-btn">Consultation</router-link>
                  </div>
                  <div v-if="group.showTags !== false" class="consultant-tags">
                    <span v-for="tag in member.tags" :key="`${member.name}-${tag}`">{{ tag }}</span>
                  </div>
                </div>
              </template>
            </article>
          </div>
        </article>
      </div>
    </section>

    <section class="team-bottom-deco">
      <div class="bottom-shell">
        <h3>Need a consultant match?</h3>
        <p>Tell us your budget and objectives, and we will match you with the best consultant combination.</p>
        <router-link to="/contact" class="bottom-btn">Book a Discovery Call</router-link>
      </div>
    </section>

    <SiteFooter />
  </div>
</template>

<script>
import SiteFooter from '@/components/layout/SiteFooter.vue'
import Navbar from '@/components/layout/Navbar.vue'
import env from '@/api/env'
import { pubGetSupportingTeams, pubGetTeamMembers } from '@/api/apiService'

export default {
  components: { SiteFooter, Navbar },
  data() {
    return {
      teamGroups: [
        {
          code: 'a) Norland Board',
          title: 'Norland Board',
          accent: '#4f9887',
          soft: '#e4f3ef',
          deep: '#2c6b5d',
          members: [
            {
              name: 'Lemon Xu',
              role: 'Founder & Managing Director',
              bio: 'Leads research-driven property strategy and long-cycle portfolio execution in Australia.',
              details: [
                'PhD-level research background, Monash University',
                'Master of Philosophy (MPhil), Monash University',
                'Bachelor of Engineering (Honours), Monash University',
                'Established a professional property portfolio from $350,000 in 2021 to 13 properties with total value exceeding $8 million.',
                'Specializes in Australian property market research, including market cycles, location fundamentals, and suburb-level data analysis.'
              ],
              rednoteLink: 'https://www.xiaohongshu.com/user/profile/67d8b1ff000000000e012433?xsec_token=AB3LrZutLV_VQH13y7KLghqM3glGUwvd2kZJRl5iKejnM%3D&xsec_source=pc_search',
              tags: ['Strategy', 'Leadership', 'Final Review'],
              photo: '/images/lemont1.jpg'
            },
            {
              name: 'Ray Liu',
              role: 'Founder & Executive Director',
              bio: 'Believes property investment should not be limited to the wealthy.',
              details: [
                'Master of Construction Management, The University of Melbourne',
                'Starting with $200,000 in 2018, he has built a portfolio of 14 properties worth over $10 million.',
                'Specializes in helping everyday people go from 0 to 1 using strategic portfolio planning and smart property acquisition to build long-term wealth and financial freedom.'
              ],
              rednoteLink: 'https://www.xiaohongshu.com/user/profile/5b7bbe3cbb095a00012beb10?xsec_token=ABwqD7PrFR24s_Eq2b4VclgICsqSmTH4Btenrn4biDgYw%3D&xsec_source=pc_search',
              tags: ['0 to 1 Strategy', 'Portfolio Planning', 'Acquisition'],
              photo: '/images/rayliut22.jpg'
            },
            {
              name: 'Terry Ryder',
              role: 'Founder, Hotspotting',
              bio: 'Independent property market commentator and research leader focused on Australian growth locations.',
              details: [
                'Founder of Hotspotting.',
                'Provides independent research support for location and timing decisions.'
              ],
              tags: ['Research', 'Market Insight'],
              photo: '/images/terryryder.jpg'
            },
            {
              name: 'Tim Graham',
              role: 'General Manager, Hotspotting',
              bio: 'Leads market intelligence operations and supports practical data-driven portfolio decisions.',
              details: [
                'General Manager at Hotspotting.',
                'Supports suburb-level analysis and market trend interpretation.'
              ],
              tags: ['Operations', 'Market Intelligence'],
              photo: '/images/timgraham.jpg'
            }
          ]
        },
        {
          code: 'b) Norland Team',
          title: 'Norland Team',
          accent: '#4a8e97',
          soft: '#e2f1f4',
          deep: '#2b646c',
          layout: 'avatar-circle',
          showBio: false,
          showDetails: false,
          showTags: false,
          showRednote: false,
          showConsultButton: false,
          members: []
        },
        {
          code: 'c) Supporting Professionals',
          title: 'Supporting Professionals',
          accent: '#5f9e8f',
          soft: '#e5f4ef',
          deep: '#356e61',
          noPhoto: true,
          layout: 'flat4',
          showBio: false,
          showDetails: false,
          showTags: false,
          showRednote: false,
          showConsultButton: false,
          members: []
        }
      ]
    }
  },
  created() {
    this.fetchNorlandTeamMembers()
    this.fetchSupportingProfessionals()
  },
  methods: {
    async fetchNorlandTeamMembers() {
      try {
        const ret = await pubGetTeamMembers(env.storeId)
        const members = this.normalizeNorlandTeamMembers(ret.teamMembers)
        this.applyNorlandTeamMembers(members)
      } catch(error) {
        console.error('Failed to load Norland Team members by pubGetTeamMembers:', error)
      }
    },
    normalizeNorlandTeamMembers(source) {
      const raw = source && typeof source === 'object'
        ? (source.data && typeof source.data === 'object' ? source.data : source)
        : {}

      const list = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw.lst)
          ? raw.lst
          : (Array.isArray(raw.members)
            ? raw.members
            : []))

      return list
        .map((item) => {
          const member = item && typeof item === 'object' ? item : {}
          const name = member.name || member.memberName || member.fullName || ''
          const role = member.role || member.position || member.title || ''
          const photo = member.imageUrl || ''

          if(!name || !role) {
            return null
          }

          return {
            name,
            role,
            photo,
            desc: member.desc || member.description || member.bio || member.intro || member.introduction || '',
            rednoteLink: member.rednoteLink || member.rednote || member.xiaohongshuLink || member.xhsLink || ''
          }
        })
        .filter((item) => item)
    },
    applyNorlandTeamMembers(members) {
      this.teamGroups = this.teamGroups.map((group) => {
        if(group.title !== 'Norland Team') {
          return group
        }

        return {
          ...group,
          members
        }
      })
    },
    async fetchSupportingProfessionals() {
      try {
        const ret = await pubGetSupportingTeams(env.storeId)
        const members = this.normalizeSupportingProfessionals(ret.supportingTeams)
        this.applySupportingProfessionals(members)
      } catch(error) {
        console.error('Failed to load Supporting Professionals by pubGetSupportingTeams:', error)
      }
    },
    normalizeSupportingProfessionals(source) {
      const raw = source && typeof source === 'object'
        ? (source.data && typeof source.data === 'object' ? source.data : source)
        : {}

      const list = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw.lst)
          ? raw.lst
          : (Array.isArray(raw.members)
            ? raw.members
            : []))

      return list
        .map((item) => {
          const member = item && typeof item === 'object' ? item : {}
          const name = member.name || member.memberName || member.fullName || ''
          const role = member.role || member.position || member.title || ''

          if(!name || !role) {
            return null
          }

          return {
            name,
            role
          }
        })
        .filter((item) => item)
    },
    applySupportingProfessionals(members) {
      this.teamGroups = this.teamGroups.map((group) => {
        if(group.title !== 'Supporting Professionals') {
          return group
        }

        return {
          ...group,
          members
        }
      })
    },
    getInitials(name) {
      if(!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('')
    },
    hasRednoteLink(member) {
      const link = member && member.rednoteLink ? String(member.rednoteLink).trim() : ''
      return !!link
    },
    getRednoteLink(member) {
      return member && member.rednoteLink ? String(member.rednoteLink).trim() : ''
    }
  }
}
</script>

<style scoped>
.team-top-deco {
  position: relative;
  height: 120px;
  background: linear-gradient(180deg, #f1f8f6 0%, #e8f3f0 100%);
  overflow: hidden;
}

.deco-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(2px);
}

.orb-left {
  width: 180px;
  height: 180px;
  left: -34px;
  top: -70px;
  background: radial-gradient(circle at 30% 30%, rgba(154, 203, 190, 0.72), rgba(154, 203, 190, 0));
}

.orb-right {
  width: 220px;
  height: 220px;
  right: -60px;
  top: -96px;
  background: radial-gradient(circle at 36% 30%, rgba(86, 146, 132, 0.5), rgba(86, 146, 132, 0));
}

.deco-line {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -18px;
  width: min(92vw, 1160px);
  height: 52px;
  border: 1px solid rgba(138, 187, 174, 0.45);
  border-top-left-radius: 100% 100%;
  border-top-right-radius: 100% 100%;
  border-bottom: none;
}

.team-hero {
  text-align: center;
  padding: 34px 5% 28px;
  background: #e8f3f0;
}

.hero-kicker {
  margin: 0;
  color: #4b8f80;
  font-size: 0.82rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}

.team-hero h1 {
  margin: 12px 0 10px;
  color: #2f5f55;
  font-size: clamp(2.1rem, 5vw, 4rem);
}

.team-hero p {
  margin: 0 auto;
  max-width: 800px;
  color: #5f7f77;
  line-height: 1.7;
}

.team-groups-section {
  padding: 42px 5% 70px;
  background:
    radial-gradient(circle at 10% 15%, rgba(133, 187, 173, 0.12), transparent 35%),
    radial-gradient(circle at 88% 78%, rgba(157, 206, 193, 0.14), transparent 34%),
    #e9f3f0;
}

.team-groups-shell {
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  gap: 22px;
}

.team-group-card {
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--group-accent) 24%, #ffffff 76%);
  background: #fffdfa;
  overflow: visible;
  box-shadow: 0 10px 22px rgba(87, 57, 34, 0.08);
}

.group-header {
  padding: 16px 20px;
  background: linear-gradient(120deg, var(--group-soft), #fffdf8 68%);
  border-bottom: 1px solid color-mix(in srgb, var(--group-accent) 24%, #ffffff 76%);
  border-top-left-radius: 17px;
  border-top-right-radius: 17px;
}

.group-header p {
  margin: 0;
  color: var(--group-accent);
  font-size: 0.82rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 700;
}

.group-header h2 {
  margin: 8px 0 0;
  color: var(--group-deep);
  font-size: clamp(1.2rem, 2vw, 1.8rem);
}

.group-member-list {
  padding: 14px;
  display: grid;
  gap: 12px;
}

.group-member-list-flat4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.group-member-list-flat4 .consultant-card {
  grid-template-columns: 1fr;
}

.group-member-list-flat4 .consultant-photo-wrap {
  width: 275px;
  height: 317px;
  min-height: 317px;
  margin: 0 auto;
}

.group-member-list-flat4 .consultant-content {
  padding: 14px 14px 16px;
}

.group-member-list-flat4 .consultant-content h3 {
  font-size: 1.02rem;
}

.group-member-list-flat4 .consultant-role {
  font-size: 0.92rem;
}

.consultant-card {
  display: grid;
  grid-template-columns: minmax(160px, 210px) 1fr;
  align-items: stretch;
  border-radius: 14px;
  overflow: hidden;
  background: #fffdfa;
  border: 1px solid color-mix(in srgb, var(--group-accent) 24%, #ffffff 76%);
}

.consultant-card-board {
  display: block;
  padding: 16px 18px;
}

.board-top {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 20px;
  align-items: center;
}

.board-avatar {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--group-accent, #4f9887);
  border: 3px solid #ffffff;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--group-accent, #4f9887) 22%, #ffffff 78%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.board-identity h3 {
  margin: 0;
}

.board-identity {
  margin-left: 2px;
}

.board-identity .consultant-role {
  margin: 6px 0 0;
}

.consultant-content-board {
  padding: 14px 0 0;
}

.consultant-card-basic {
  grid-template-columns: 1fr;
  background: linear-gradient(120deg, #fffefa 0%, var(--group-soft) 100%);
}

.consultant-card-basic .consultant-content {
  padding: 16px 18px;
}

.consultant-card-basic .consultant-role {
  margin-bottom: 0;
}

.consultant-photo-wrap {
  min-height: 190px;
  background: var(--group-accent);
}

.consultant-photo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.consultant-content {
  padding: 18px 20px;
}

.consultant-content h3 {
  margin: 0;
  color: #3e2d1f;
  font-size: clamp(1.08rem, 1.6vw, 1.4rem);
}

.consultant-role {
  margin: 7px 0 10px;
  color: var(--group-accent);
  font-weight: 600;
  letter-spacing: 0.03em;
}

.consultant-bio {
  margin: 0;
  color: #6a5441;
  line-height: 1.65;
}

.consultant-details {
  margin: 10px 0 0;
  padding-left: 18px;
  color: #5e4b3b;
  line-height: 1.55;
}

.consultant-details li+li {
  margin-top: 4px;
}

.consultant-links {
  margin-top: 10px;
}

.consultant-links-placeholder {
  min-height: 24px;
}

.consultant-links a {
  color: var(--group-deep);
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--group-accent) 62%, #ffffff 38%);
}

.consultant-links a:hover {
  text-decoration-color: var(--group-accent);
}

.consultant-actions {
  margin-top: 10px;
}

.consultant-consult-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 9px;
  text-decoration: none;
  color: #ffffff;
  background: var(--group-accent);
  font-weight: 700;
  font-size: 0.85rem;
}

.consultant-consult-btn:hover {
  filter: brightness(0.95);
}

.consultant-tags {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.consultant-tags span {
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 0.76rem;
  color: var(--group-deep);
  background: var(--group-soft);
  border: 1px solid color-mix(in srgb, var(--group-accent) 26%, #ffffff 74%);
}

.group-member-list-avatars {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px 12px;
  padding: 22px 18px;
}

.avatar-member {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  padding: 10px 8px;
}

.avatar-member-has-popover {
  cursor: pointer;
  outline: none;
}

.member-popover {
  position: absolute;
  left: 50%;
  top: calc(100% + 14px);
  transform: translateX(-50%) translateY(-6px) scale(0.96);
  transform-origin: top center;
  width: min(86vw, 340px);
  max-width: calc(100vw - 24px);
  padding: 18px 18px 16px;
  border-radius: 18px;
  background:
    linear-gradient(180deg, #ffffff 0%, color-mix(in srgb, var(--group-soft, #e2f1f4) 70%, #ffffff 30%) 100%);
  border: 1px solid color-mix(in srgb, var(--group-accent, #4a8e97) 22%, #ffffff 78%);
  box-shadow:
    0 24px 48px rgba(20, 64, 56, 0.18),
    0 4px 10px rgba(20, 64, 56, 0.08);
  text-align: left;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease, transform 0.22s ease;
  z-index: 10;
}

.member-popover::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -7px;
  transform: translateX(-50%) rotate(45deg);
  width: 14px;
  height: 14px;
  background: #ffffff;
  border-left: 1px solid color-mix(in srgb, var(--group-accent, #4a8e97) 22%, #ffffff 78%);
  border-top: 1px solid color-mix(in srgb, var(--group-accent, #4a8e97) 22%, #ffffff 78%);
  border-radius: 3px;
}

/* Invisible bridge that keeps hover state while moving from avatar to popover */
.member-popover::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -18px;
  height: 18px;
  background: transparent;
}

.avatar-member-has-popover:hover .member-popover,
.avatar-member-has-popover:focus-within .member-popover,
.avatar-member-has-popover:focus-visible .member-popover {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0) scale(1);
}

/* Keep popover inside viewport for edge columns of the avatar grid */
.group-member-list-avatars>.avatar-member:nth-child(5n + 1) .member-popover {
  left: 0;
  transform: translateX(0) translateY(-6px) scale(0.96);
}

.group-member-list-avatars>.avatar-member:nth-child(5n + 1):hover .member-popover,
.group-member-list-avatars>.avatar-member:nth-child(5n + 1):focus-within .member-popover,
.group-member-list-avatars>.avatar-member:nth-child(5n + 1):focus-visible .member-popover {
  transform: translateX(0) translateY(0) scale(1);
}

.group-member-list-avatars>.avatar-member:nth-child(5n + 1) .member-popover::before {
  left: 56px;
}

.group-member-list-avatars>.avatar-member:nth-child(5n) .member-popover {
  left: auto;
  right: 0;
  transform: translateX(0) translateY(-6px) scale(0.96);
}

.group-member-list-avatars>.avatar-member:nth-child(5n):hover .member-popover,
.group-member-list-avatars>.avatar-member:nth-child(5n):focus-within .member-popover,
.group-member-list-avatars>.avatar-member:nth-child(5n):focus-visible .member-popover {
  transform: translateX(0) translateY(0) scale(1);
}

.group-member-list-avatars>.avatar-member:nth-child(5n) .member-popover::before {
  left: auto;
  right: 56px;
  transform: rotate(45deg);
}

.member-popover-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.member-popover-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--group-accent, #4a8e97);
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--group-accent, #4a8e97) 30%, #ffffff 70%);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
}

.member-popover-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.member-popover-identity {
  min-width: 0;
  flex: 1;
}

.member-popover-identity h4 {
  margin: 0;
  font-size: 1.02rem;
  color: var(--group-deep, #2b646c);
  line-height: 1.3;
  word-break: break-word;
}

.member-popover-role {
  margin: 4px 0 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--group-accent, #4a8e97);
  letter-spacing: 0.02em;
  line-height: 1.4;
  word-break: break-word;
}

.member-popover-desc {
  margin: 14px 0 0;
  padding-top: 12px;
  border-top: 1px dashed color-mix(in srgb, var(--group-accent, #4a8e97) 26%, #ffffff 74%);
  color: #46645d;
  font-size: 0.82rem;
  line-height: 1.7;
  white-space: pre-line;
  word-break: break-word;
}

.member-popover-link {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff2741 0%, #ff5e6c 100%);
  color: #ffffff;
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 16px rgba(255, 39, 65, 0.28);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.member-popover-link:hover,
.member-popover-link:focus-visible {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(255, 39, 65, 0.34);
  filter: brightness(1.03);
}

.member-popover-link-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  color: #ff2741;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
}

.avatar-circle-wrap {
  position: relative;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--group-accent, #4a8e97);
  border: 3px solid #ffffff;
  box-shadow: 0 0 0 1px #e6e6e6;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.avatar-member:hover .avatar-circle-wrap,
.avatar-member:focus-within .avatar-circle-wrap {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(40, 96, 84, 0.22);
}

.avatar-circle-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-circle-initials {
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1;
}

.avatar-hover-cta {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%) translateY(8px);
  opacity: 0;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  background: rgba(12, 43, 37, 0.72);
  color: #ffffff;
  text-decoration: none;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  backdrop-filter: blur(2px);
  transition: opacity 0.2s ease, transform 0.2s ease, background 0.2s ease;
}

.avatar-member:hover .avatar-hover-cta,
.avatar-member:focus-within .avatar-hover-cta {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

.avatar-hover-cta:hover,
.avatar-hover-cta:focus-visible {
  background: rgba(16, 58, 50, 0.86);
}

.avatar-member-name {
  margin: 12px 0 4px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #2f2f2f;
  line-height: 1.3;
  word-break: break-word;
}

.avatar-member-role {
  margin: 0;
  font-size: 0.74rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
  opacity: 0.85;
}

.team-bottom-deco {
  padding: 36px 5% 58px;
  background: linear-gradient(180deg, #e9f3f0 0%, #eef7f4 100%);
}

.bottom-shell {
  max-width: 1180px;
  margin: 0 auto;
  border-radius: 18px;
  border: 1px solid #cfe2dc;
  padding: 22px 24px;
  background:
    radial-gradient(circle at 10% 20%, rgba(136, 191, 176, 0.18), transparent 34%),
    #f7fcfa;
}

.bottom-shell h3 {
  margin: 0;
  color: #2f5f55;
  font-size: clamp(1.35rem, 2.5vw, 2rem);
}

.bottom-shell p {
  margin: 8px 0 0;
  color: #5f7f77;
}

.bottom-btn {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 10px;
  text-decoration: none;
  color: #fff;
  background: #4d8d7d;
  font-weight: 700;
}

@media (max-width: 1100px) {
  .group-member-list-flat4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .group-member-list-avatars {
    grid-template-columns: repeat(4, 1fr);
  }

  /* Reset 5-col edge rules and apply 4-col */
  .group-member-list-avatars>.avatar-member:nth-child(n) .member-popover {
    left: 50%;
    right: auto;
    transform: translateX(-50%) translateY(-6px) scale(0.96);
  }

  .group-member-list-avatars>.avatar-member:nth-child(n):hover .member-popover,
  .group-member-list-avatars>.avatar-member:nth-child(n):focus-within .member-popover,
  .group-member-list-avatars>.avatar-member:nth-child(n):focus-visible .member-popover {
    transform: translateX(-50%) translateY(0) scale(1);
  }

  .group-member-list-avatars>.avatar-member:nth-child(n) .member-popover::before {
    left: 50%;
    right: auto;
  }

  .group-member-list-avatars>.avatar-member:nth-child(4n + 1) .member-popover {
    left: 0;
    transform: translateX(0) translateY(-6px) scale(0.96);
  }

  .group-member-list-avatars>.avatar-member:nth-child(4n + 1):hover .member-popover,
  .group-member-list-avatars>.avatar-member:nth-child(4n + 1):focus-within .member-popover,
  .group-member-list-avatars>.avatar-member:nth-child(4n + 1):focus-visible .member-popover {
    transform: translateX(0) translateY(0) scale(1);
  }

  .group-member-list-avatars>.avatar-member:nth-child(4n + 1) .member-popover::before {
    left: 56px;
  }

  .group-member-list-avatars>.avatar-member:nth-child(4n) .member-popover {
    left: auto;
    right: 0;
    transform: translateX(0) translateY(-6px) scale(0.96);
  }

  .group-member-list-avatars>.avatar-member:nth-child(4n):hover .member-popover,
  .group-member-list-avatars>.avatar-member:nth-child(4n):focus-within .member-popover,
  .group-member-list-avatars>.avatar-member:nth-child(4n):focus-visible .member-popover {
    transform: translateX(0) translateY(0) scale(1);
  }

  .group-member-list-avatars>.avatar-member:nth-child(4n) .member-popover::before {
    left: auto;
    right: 56px;
  }
}

@media (max-width: 768px) {
  .team-top-deco {
    height: 98px;
  }

  .team-hero {
    padding: 24px 4% 20px;
  }

  .team-groups-section {
    padding: 28px 4% 46px;
  }

  .group-header {
    padding: 14px 16px;
  }

  .consultant-card {
    grid-template-columns: 1fr;
  }

  .consultant-photo-wrap {
    min-height: 230px;
  }

  .consultant-card-board {
    padding: 14px;
  }

  .board-top {
    grid-template-columns: 78px 1fr;
    gap: 16px;
  }

  .board-avatar {
    width: 78px;
    height: 78px;
  }

  .consultant-content {
    padding: 16px;
  }

  .group-member-list-flat4 {
    grid-template-columns: 1fr;
  }

  .group-member-list-avatars {
    grid-template-columns: repeat(3, 1fr);
  }

  .group-member-list-flat4 .consultant-photo-wrap {
    width: 100%;
    height: auto;
    min-height: 0;
  }

  .group-member-list-flat4 .consultant-photo {
    height: auto;
    aspect-ratio: 4 / 5;
  }

  .team-bottom-deco {
    padding: 22px 4% 36px;
  }

  .bottom-shell {
    padding: 18px;
  }
}
</style>
